import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '../Theme/ThemeProvider';
import { Cross2Icon, ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import {
  exitTutorial,
  goToStep,
  CHAT_ONLY_STEP_IDS,
  PLUS_MENU_STEP_IDS,
  type TutorialStep,
} from '../../store/tutorialSlice';
import { markTutorialAsCompleted } from '../../utils/tutorialStorage';
import logger from '../../utils/logger';
import type { RootState } from '../../store/store';

export function GuidedTutorial() {
  const dispatch = useDispatch();
  const { isActive, steps, currentStepIndex } = useSelector((state: RootState) => state.tutorial);
  const chatMode = useSelector((state: RootState) => state.chatMode.mode);

  // Filter steps based on active chat mode
  const filteredSteps = useMemo(() => {
    if (chatMode === '3M Knowledge') {
      return steps.filter(s => !CHAT_ONLY_STEP_IDS.has(s.id));
    }
    return steps;
  }, [steps, chatMode]);

  // Map filtered positions back to original step indices
  const filteredStepIndices = useMemo(() => {
    return filteredSteps.map(fs => steps.findIndex(s => s.id === fs.id));
  }, [filteredSteps, steps]);

  // Current position within filtered list
  const filteredPosition = useMemo(() => {
    const pos = filteredStepIndices.indexOf(currentStepIndex);
    return pos >= 0 ? pos : 0;
  }, [filteredStepIndices, currentStepIndex]);

  const currentStep = filteredSteps[filteredPosition];
  const totalSteps = filteredSteps.length;

  const onNext = useCallback(() => {
    const nextPos = filteredPosition + 1;
    if (nextPos >= filteredSteps.length) {
      markTutorialAsCompleted();
      dispatch(exitTutorial());
      setTargetRect(null);
      setDisplayedStep(undefined);
      setTooltipReady(false);
      setArrowReady(false);
    } else {
      dispatch(goToStep(filteredStepIndices[nextPos]));
    }
  }, [dispatch, filteredPosition, filteredSteps.length, filteredStepIndices]);

  const onPrevious = useCallback(() => {
    const prevPos = filteredPosition - 1;
    if (prevPos >= 0) {
      dispatch(goToStep(filteredStepIndices[prevPos]));
    }
  }, [dispatch, filteredPosition, filteredStepIndices]);

  const onExit = useCallback(() => {
    dispatch(exitTutorial());
    markTutorialAsCompleted();
    setTargetRect(null);
    setDisplayedStep(undefined);
    setTooltipReady(false);
    setArrowReady(false);
  }, [dispatch]);
  const { mode, accent } = useTheme();
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [tooltipArrowPosition, setTooltipArrowPosition] = useState<'top' | 'bottom' | 'left' | 'right'>('right');
  const [arrowOffset, setArrowOffset] = useState<number>(0); // For aligning arrow with target on mobile
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [plusRect, setPlusRect] = useState<DOMRect | null>(null);
  const [arrowReady, setArrowReady] = useState(false); // Arrow positioned after content updates
  const [tooltipReady, setTooltipReady] = useState(false); // Tooltip positioned correctly
  const [displayedStep, setDisplayedStep] = useState<TutorialStep | undefined>(currentStep); // Delayed step for text content
  const [isTransitioning, setIsTransitioning] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const previousHeightRef = useRef<number>(0); // Track previous tooltip height
  
  // Detect mobile/tablet
  const isMobileOrTablet = window.innerWidth < 1024;

  // Get accent color for styling
  const getAccentColor = (accentColor: string) => {
    if (accentColor === 'purple') return mode === 'dark' ? '#a78bfa' : '#8b5cf6';
    if (accentColor === 'blue') return mode === 'dark' ? '#60a5fa' : '#3b82f6';
    if (accentColor === 'green') return mode === 'dark' ? '#34d399' : '#10b981';
    if (accentColor === 'gold') return mode === 'dark' ? '#fbbf24' : '#f59e0b';
    return mode === 'dark' ? '#60a5fa' : '#3b82f6'; // blue as default
  };

  const accentColor = getAccentColor(accent);

  // Handle delayed text update when step changes
  useEffect(() => {
    if (!isActive) return;
    
    // If we're on step 0 (restart or initial), update immediately
    if (filteredPosition === 0) {
      setDisplayedStep(currentStep);
      setIsTransitioning(false);
      return;
    }
    
    // Otherwise, fade out text content
    setIsTransitioning(true);
    
    // Wait for spotlight to move, then update text and fade in
    const timer = setTimeout(() => {
      setDisplayedStep(currentStep);
      setIsTransitioning(false);
    }, 300); // 300ms delay matches spotlight transition
    
    return () => clearTimeout(timer);
  }, [currentStep, filteredPosition, isActive]);

  // Hide arrow immediately when step index changes
  useEffect(() => {
    if (!isActive) return;
    setArrowReady(false);
    setTooltipReady(false);
    
    // Reset previous height when restarting tour (going to step 0)
    // This prevents transition from old position
    if (filteredPosition === 0) {
      previousHeightRef.current = 0;
    }
  }, [filteredPosition, isActive]);

  useEffect(() => {
    if (!isActive || !currentStep?.targetElement) return;

    const updatePosition = () => {
      // Use mobile target element if on mobile/tablet and it exists, otherwise use default
      const targetElementSelector = isMobileOrTablet && currentStep.mobileTargetElement 
        ? currentStep.mobileTargetElement 
        : currentStep.targetElement;
      
      logger.log('[GuidedTutorial] Looking for element:', targetElementSelector, 'isMobile:', isMobileOrTablet);
      
      const allMatches = Array.from(document.querySelectorAll(`[data-tutorial="${targetElementSelector}"]`));
      const targetElement = allMatches.find(el => {
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
      }) ?? allMatches[0] ?? null;
      
      logger.log('[GuidedTutorial] Found element:', targetElement);
      
      // Track plus button rect for plus-menu steps so it stays undarked
      if (currentStep?.id && PLUS_MENU_STEP_IDS.has(currentStep.id)) {
        const plusEl = document.querySelector('[data-tutorial="plus-menu-button"]');
        setPlusRect(plusEl ? plusEl.getBoundingClientRect() : null);
      } else {
        setPlusRect(null);
      }

      if (targetElement && tooltipRef.current) {
        const rect = targetElement.getBoundingClientRect();
        setTargetRect(rect);
        
        logger.log('[GuidedTutorial] Target rect:', rect);
        
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const padding = 20;
        
        let top = 0;
        let left = 0;
        let arrowPos: 'top' | 'bottom' | 'left' | 'right' = 'right';

        // Calculate position based on preferred position
        // For mobile/tablet, prefer top/bottom positioning if element is near bottom of screen
        let position = currentStep.position || 'right';
        
        // Use mobile-specific position if on mobile and it exists
        if (isMobileOrTablet && currentStep.mobilePosition) {
          position = currentStep.mobilePosition;
        } else if (isMobileOrTablet && rect.bottom > window.innerHeight - 200) {
          // Element is near bottom on mobile, use top positioning
          position = 'top';
        }
        
        switch (position) {
          case 'right':
            top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
            left = rect.right + padding;
            arrowPos = 'left';
            
            // Check if tooltip goes off screen
            if (left + tooltipRect.width > window.innerWidth - padding) {
              // Switch to left
              left = rect.left - tooltipRect.width - padding;
              arrowPos = 'right';
            }
            break;
            
          case 'left':
            top = rect.top + (rect.height / 2) - (tooltipRect.height / 2);
            left = rect.left - tooltipRect.width - padding;
            arrowPos = 'right';
            
            if (left < padding) {
              // Switch to right
              left = rect.right + padding;
              arrowPos = 'left';
            }
            break;
            
          case 'bottom':
            top = rect.bottom + padding;
            left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
            arrowPos = 'top';
            
            if (top + tooltipRect.height > window.innerHeight - padding) {
              // Switch to top
              top = rect.top - tooltipRect.height - padding;
              arrowPos = 'bottom';
            }
            break;
            
          case 'top':
            top = rect.top - tooltipRect.height - padding;
            left = rect.left + (rect.width / 2) - (tooltipRect.width / 2);
            arrowPos = 'bottom';
            
            if (top < padding) {
              // Switch to bottom
              top = rect.bottom + padding;
              arrowPos = 'top';
            }
            break;
        }

        // Ensure tooltip stays on screen
        if (top < padding) top = padding;
        if (top + tooltipRect.height > window.innerHeight - padding) {
          top = window.innerHeight - tooltipRect.height - padding;
        }
        if (left < padding) left = padding;
        if (left + tooltipRect.width > window.innerWidth - padding) {
          left = window.innerWidth - tooltipRect.width - padding;
        }

        // Calculate arrow offset AFTER clamping to use final tooltip position
        let calculatedArrowOffset = 0;
        if (arrowPos === 'top' || arrowPos === 'bottom') {
          // Arrow should point to center of target element horizontally
          const targetCenter = rect.left + (rect.width / 2);
          calculatedArrowOffset = targetCenter - left - (tooltipRect.width / 2);
          // Clamp offset so arrow stays within tooltip bounds
          const maxOffset = (tooltipRect.width / 2) - 40; // Keep 40px from edges
          calculatedArrowOffset = Math.max(-maxOffset, Math.min(maxOffset, calculatedArrowOffset));
        } else if (arrowPos === 'left' || arrowPos === 'right') {
          // Arrow should point to center of target element vertically
          const targetCenter = rect.top + (rect.height / 2);
          calculatedArrowOffset = targetCenter - top - (tooltipRect.height / 2);
          // Clamp offset so arrow stays within tooltip bounds
          const maxOffset = (tooltipRect.height / 2) - 40; // Keep 40px from edges
          calculatedArrowOffset = Math.max(-maxOffset, Math.min(maxOffset, calculatedArrowOffset));
        }

        setTooltipPosition({ top, left });
        setTooltipArrowPosition(arrowPos);
        setArrowOffset(calculatedArrowOffset);
        
        logger.log('[GuidedTutorial] Tooltip position set:', { top, left }, 'Arrow pos:', arrowPos, 'Position type:', position);
      }
    };

    // Reset arrow visibility when step changes
    setArrowReady(false); // Hide arrow until final recalculation
    setTooltipReady(false); // Hide tooltip until final positioning
    
    // Initial position update with slight delay to ensure DOM is ready
    const timer1 = setTimeout(updatePosition, 50);
    // Second update to catch any layout shifts
    const timer2 = setTimeout(updatePosition, 150);
    // Third update after content has changed (300ms is when displayedStep updates)
    const timer3 = setTimeout(updatePosition, 350);
    
    // Update position on scroll and resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isActive, currentStep, mode, isMobileOrTablet]);

  // Recalculate position when displayed step changes (after content updates)
  useEffect(() => {
    if (!isActive || !currentStep || !tooltipRef.current) return;
    
    const recalculate = () => {
      // Use mobile target element if on mobile/tablet and it exists, otherwise use default
      const targetElementSelector = isMobileOrTablet && currentStep.mobileTargetElement 
        ? currentStep.mobileTargetElement 
        : currentStep.targetElement;
      
      const allMatchesR = Array.from(document.querySelectorAll(`[data-tutorial="${targetElementSelector}"]`));
      const targetElement = allMatchesR.find(el => {
        const r = el.getBoundingClientRect();
        return r.width > 0 && r.height > 0;
      }) ?? allMatchesR[0] ?? null;
      if (!targetElement) return;
      
      logger.log('[GuidedTutorial] Recalculating for element:', targetElementSelector);
      
      const rect = targetElement.getBoundingClientRect();
      const tooltipRect = tooltipRef.current!.getBoundingClientRect();
      
      // Recalculate arrow offset with updated tooltip dimensions
      let calculatedArrowOffset = 0;
      if (tooltipArrowPosition === 'top' || tooltipArrowPosition === 'bottom') {
        const targetCenter = rect.left + (rect.width / 2);
        calculatedArrowOffset = targetCenter - tooltipPosition.left - (tooltipRect.width / 2);
        const maxOffset = (tooltipRect.width / 2) - 40;
        calculatedArrowOffset = Math.max(-maxOffset, Math.min(maxOffset, calculatedArrowOffset));
      } else if (tooltipArrowPosition === 'left' || tooltipArrowPosition === 'right') {
        const targetCenter = rect.top + (rect.height / 2);
        calculatedArrowOffset = targetCenter - tooltipPosition.top - (tooltipRect.height / 2);
        const maxOffset = (tooltipRect.height / 2) - 40;
        calculatedArrowOffset = Math.max(-maxOffset, Math.min(maxOffset, calculatedArrowOffset));
      }
      
      setArrowOffset(calculatedArrowOffset);
      
      // Show tooltip immediately, but delay arrow slightly to ensure perfect positioning
      setTooltipReady(true);
      setTimeout(() => setArrowReady(true), 50);
      
      // Store current height for next transition
      previousHeightRef.current = tooltipRect.height;
    };
    
    // Check if tooltip height has changed significantly
    const currentHeight = tooltipRef.current?.getBoundingClientRect().height || 0;
    const heightChanged = Math.abs(currentHeight - previousHeightRef.current) > 10;
    
    // Use shorter delay if height hasn't changed (navigating between similar-sized tooltips)
    const delay = heightChanged ? 100 : 20;
    const timer = setTimeout(recalculate, delay);
    return () => clearTimeout(timer);
  }, [displayedStep, isActive, currentStep, isMobileOrTablet, tooltipArrowPosition, tooltipPosition]);

  if (!isActive || !currentStep) return null;

  // Guided tour is desktop-only
  if (isMobileOrTablet) return null;

  const isFirstStep = filteredPosition === 0;
  const isLastStep = filteredPosition === totalSteps - 1;

  return (
    <>
      {/* Dark overlay with SVG mask — punches holes for the target and (on plus-menu steps) the plus button */}
      {targetRect && (
        <svg
          className="fixed inset-0 pointer-events-none"
          style={{ zIndex: 9995, width: '100vw', height: '100vh' }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <mask id="tutorial-spotlight-mask">
              {/* Full white = darken everything */}
              <rect x="0" y="0" width="100%" height="100%" fill="white" />
              {/* Black = cut out (transparent) for the target element */}
              <rect
                x={targetRect.left - 8}
                y={targetRect.top - 8}
                width={targetRect.width + 16}
                height={targetRect.height + 16}
                rx="8"
                fill="black"
              />
              {/* Black = cut out for the plus button on plus-menu steps */}
              {plusRect && (
                <rect
                  x={plusRect.left - 4}
                  y={plusRect.top - 4}
                  width={plusRect.width + 8}
                  height={plusRect.height + 8}
                  rx="6"
                  fill="black"
                />
              )}
            </mask>
          </defs>
          <rect x="0" y="0" width="100%" height="100%" fill="rgba(0,0,0,0.75)" mask="url(#tutorial-spotlight-mask)" />
        </svg>
      )}

      {/* Spotlight border ring — above the dropdown so it's always visible */}
      {targetRect && (
        <div
          className="fixed pointer-events-none"
          style={{
            zIndex: 10001,
            top: targetRect.top - 8,
            left: targetRect.left - 8,
            width: targetRect.width + 16,
            height: targetRect.height + 16,
            backgroundColor: 'transparent',
            border: `3px solid ${accentColor}`,
            borderRadius: '8px',
            boxShadow: `0 0 20px ${accentColor}80, inset 0 0 0 1px ${accentColor}40`,
          }}
        />
      )}

      {/* Clickable backdrop for closing */}
      <div 
        className="fixed inset-0 z-[9996]"
        style={{
          backgroundColor: 'transparent',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onExit();
          }
        }}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="fixed z-[9998] rounded-xl shadow-2xl"
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          width: '320px',
          maxWidth: 'calc(100vw - 40px)',
          backgroundColor: mode === 'dark' ? '#1f2937' : '#ffffff',
          border: `2px solid ${accentColor}`,
          opacity: tooltipReady ? 1 : 0,
          animation: tooltipReady ? 'tooltipSlideIn 0.3s ease-out' : 'none',
          // Disable transitions on step 0 to prevent animating from previous position
          transition: filteredPosition === 0 
            ? 'opacity 0.2s ease-in-out' 
            : 'top 0.3s ease-in-out, left 0.3s ease-in-out, width 0.3s ease-in-out, height 0.3s ease-in-out, opacity 0.2s ease-in-out'
        }}
      >
        {/* Arrow - only render when ready to prevent position jumping */}
        {arrowReady && (
          <div
            className="absolute"
            style={{
              width: '16px',
              height: '16px',
              backgroundColor: mode === 'dark' ? '#1f2937' : '#ffffff',
              opacity: 1,
              transition: 'opacity 0.2s ease-in-out',
              ...(() => {
              const borderStyle = `2px solid ${accentColor}`;
              switch (tooltipArrowPosition) {
                case 'left':
                  // Arrow on left side of tooltip, pointing left
                  return {
                    left: '-10px',
                    top: '50%',
                    marginTop: `${arrowOffset}px`,
                    transform: 'translateY(-50%) rotate(45deg)',
                    borderLeft: borderStyle,
                    borderBottom: borderStyle,
                  };
                case 'right':
                  // Arrow on right side of tooltip, pointing right
                  return {
                    right: '-10px',
                    top: '50%',
                    marginTop: `${arrowOffset}px`,
                    transform: 'translateY(-50%) rotate(45deg)',
                    borderRight: borderStyle,
                    borderTop: borderStyle,
                  };
                case 'top':
                  // Arrow on top of tooltip, pointing up
                  return {
                    top: '-10px',
                    left: '50%',
                    marginLeft: `${arrowOffset}px`,
                    transform: 'translateX(-50%) rotate(45deg)',
                    borderTop: borderStyle,
                    borderLeft: borderStyle,
                  };
                case 'bottom':
                  // Arrow on bottom of tooltip, pointing down
                  return {
                    bottom: '-10px',
                    left: '50%',
                    marginLeft: `${arrowOffset}px`,
                    transform: 'translateX(-50%) rotate(45deg)',
                    borderBottom: borderStyle,
                    borderRight: borderStyle,
                  };
              }
            })(),
          }}
        />
        )}

        {/* Content */}
        <div 
          className="relative p-5 overflow-hidden"
          style={{
            opacity: isTransitioning ? 0 : 1,
            transition: 'opacity 0.3s ease-in-out'
          }}
        >
          {/* Close button */}
          <button
            onClick={onExit}
            className="absolute top-3 right-3 rounded-full p-1.5 transition-colors"
            style={{
              color: 'var(--color-text)',
              backgroundColor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
            }}
          >
            <Cross2Icon className="w-4 h-4" />
          </button>

          {/* Step indicator */}
          <div 
            className="text-xs font-semibold mb-2"
            style={{ color: accentColor }}
          >
            Step {filteredPosition + 1} of {totalSteps}
          </div>

          {/* Title */}
          <h3 
            className="text-lg font-bold mb-3 pr-6"
            style={{ color: 'var(--color-text)' }}
          >
            {displayedStep?.title}
          </h3>

          {/* Description */}
          <p 
            className="text-sm leading-relaxed mb-4 whitespace-pre-line"
            style={{ 
              color: mode === 'dark' ? 'rgba(229, 231, 235, 0.8)' : 'rgba(55, 65, 81, 0.9)' 
            }}
          >
            {isMobileOrTablet && displayedStep?.mobileDescription 
              ? displayedStep.mobileDescription 
              : displayedStep?.description}
          </p>

          {/* Navigation buttons */}
          <div className="flex justify-between items-center gap-3 pt-2">
            <button
              onClick={onPrevious}
              disabled={isFirstStep}
              className="px-2.5 py-2 rounded-lg text-sm font-medium transition-all flex items-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                backgroundColor: mode === 'dark' ? 'rgba(75, 85, 99, 0.6)' : 'rgba(229, 231, 235, 1)',
                color: mode === 'dark' ? 'rgba(229, 231, 235, 1)' : 'rgba(31, 41, 55, 1)',
              }}
              aria-label="Previous step"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>

            <div className="flex gap-1.5 min-w-0 shrink justify-center overflow-hidden">
              {Array.from({ length: totalSteps }).map((_, index) => (
                <div
                  key={index}
                  className="rounded-full transition-all"
                  style={{
                    width: index === filteredPosition ? '24px' : '8px',
                    height: '8px',
                    backgroundColor: index === filteredPosition 
                      ? accentColor 
                      : mode === 'dark' ? 'rgba(156, 163, 175, 0.3)' : 'rgba(209, 213, 219, 1)'
                  }}
                />
              ))}
            </div>

            <button
              onClick={onNext}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center flex-shrink-0 gap-1.5"
              style={{
                backgroundColor: accentColor,
                color: '#ffffff',
              }}
            >
              {isLastStep ? 'Finish' : 'Next'}
              {!isLastStep && <ChevronRightIcon className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes tooltipSlideIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
      `}</style>
    </>
  );
}
